/**
 * ACTIVATION PAGE
 *
 * Lists organizations ready for final activation.
 * Platform Team can review and activate organizations.
 */

import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { canAccessPlatformWork } from '@/lib/team-access';
import type { Organization, Task } from '@/types/database';
import { ArrowRight, Building2, CheckCircle, Clock, Rocket } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface TaskWithDetails extends Task {
  organization: Organization;
}

export default function ActivationPage() {
  const { user, loading: authLoading } = useAuth();
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      // Check role authorization
      if (!canAccessPlatformWork(user.role)) {
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Fetch FINAL_ACTIVATION tasks at organization level (location_id is null)
        const { data: tasksData, error: tasksError } = await supabase
          .from('tasks')
          .select(
            `
            *,
            organization:organizations(id, name, status, logo_url)
          `
          )
          .eq('type', 'FINAL_ACTIVATION')
          .is('location_id', null)
          .order('status', { ascending: true })
          .order('created_at', { ascending: false });

        if (tasksError) throw tasksError;

        setTasks((tasksData as TaskWithDetails[]) || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error('Error fetching activation tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (authLoading || loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto'></div>
          <p className='mt-4 text-gray-600 dark:text-gray-400'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Card className='max-w-md'>
          <CardContent className='pt-6'>
            <p className='text-red-600 dark:text-red-400'>
              Error loading activation tasks: {error.message}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user || (user.role !== 'PLATFORM_TEAM' && user.role !== 'PLATFORM_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    return null;
  }

  // Group tasks by status
  const pendingTasks = tasks.filter((t) => t.status === 'PENDING');
  const completedTasks = tasks.filter((t) => t.status === 'COMPLETED');

  return (
    <div className='container mx-auto px-4'>
      <div className='mb-8'>
        <h1 className='text-3xl font-bold mb-2'>Final Activation</h1>
        <p className='text-muted-foreground'>
          Review and activate organizations that have completed all onboarding
          steps
        </p>
      </div>

      {/* Empty State */}
      {!tasks || tasks.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <Rocket className='h-12 w-12 text-muted-foreground mb-4' />
            <h3 className='text-lg font-semibold mb-2'>
              No activations pending
            </h3>
            <p className='text-muted-foreground text-center max-w-md'>
              Organizations will appear here once all locations complete their
              IT and data configuration tasks.
            </p>
          </CardContent>
        </Card>      ) : (
        /* Kanban Board */
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100vh-280px)] min-h-[500px] max-h-[calc(100vh-280px)] overflow-hidden'>
          {/* Pending Column */}
          <div className='flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
            <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800'>
              <div className='flex items-center gap-2'>
                <Clock className='h-5 w-5 text-yellow-600 dark:text-yellow-400' />
                <h2 className='font-semibold text-gray-900 dark:text-gray-100'>Ready for Activation</h2>
              </div>
              <span className='bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full px-2.5 py-1 text-sm font-medium'>
                {pendingTasks.length}
              </span>
            </div>
            <div className='flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500'>
              {pendingTasks.map((task) => (
                <OrganizationCard key={task.id} task={task} />
              ))}
            </div>
          </div>

          {/* Completed Column */}
          <div className='flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 h-full overflow-hidden'>
            <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800'>
              <div className='flex items-center gap-2'>
                <CheckCircle className='h-5 w-5 text-green-600 dark:text-green-400' />
                <h2 className='font-semibold text-gray-900 dark:text-gray-100'>Activated</h2>
              </div>
              <span className='bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2.5 py-1 text-sm font-medium'>
                {completedTasks.length}
              </span>
            </div>
            <div className='flex-1 overflow-y-auto p-3 space-y-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-500'>
              {completedTasks.map((task) => (
                <OrganizationCard key={task.id} task={task} />
              ))}
            </div>
          </div>
        </div>      )}
    </div>
  );
}

function OrganizationCard({ task, className }: { task: TaskWithDetails; className?: string }) {
  const isCompleted = task.status === 'COMPLETED';

  return (
    <Card
      className={`transition-all duration-200 hover:shadow-md border ${
        isCompleted
          ? 'border-green-200 dark:border-green-800 bg-white dark:bg-gray-800'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800'
      } ${className || ''}`}
    >
      <div className='p-4'>
        {/* Organization Header */}
        <div className='flex items-start gap-3 mb-3'>
          {/* Organization Logo - Smaller for Kanban */}
          {task.organization?.logo_url ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                  <img
                    src={task.organization.logo_url}
                    alt={`${task.organization.name} logo`}
                    className='w-full h-full object-cover'
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Organization Logo</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0'>
                  <Building2 className='w-6 h-6 text-gray-400' />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Organization Logo</TooltipContent>
            </Tooltip>
          )}

          {/* Organization Info */}
          <div className='flex-1 min-w-0'>
            <div className='flex items-start justify-between gap-2'>
              <h3 className='font-bold text-gray-900 dark:text-gray-100 truncate text-sm'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>{task.organization?.name}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top">Organization</TooltipContent>
                </Tooltip>
              </h3>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusBadge status={task.status} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{task.status.replace(/_/g, ' ')}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-800'>
          <div className='text-xs text-gray-600 dark:text-gray-500'>
            {isCompleted
              ? `✓ ${new Date(task.completed_at!).toLocaleDateString()}`
              : 'Ready'}
          </div>
          {!isCompleted && (
            <Button asChild size='sm' variant='default'>
              <Link to={`/activation/${task.id}`}>
                Activate
                <ArrowRight className='ml-1 h-3 w-3' />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
